/* eslint-disable no-plusplus */
import { max, reverse } from 'lodash';
import { Editor, Element, Node, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { canDeleteNode } from '../../../../../BlogEditor/plugins/shared-transforms';
import { TableElement } from '../../../table/types';
import { createTd, matchTableContent } from '../../../table/utils';

const getCellToDelete = ({ cells, allRows }) => {
  const cellsToDelete = [];
  if (cells.length > 0) {
    cells.forEach((cellRef) => {
      const baseRowIndex = cellRef.rowIndex;
      const baseColIndex = cellRef.columnIndex;

      allRows.forEach((rowNode, rowIndex) => {
        const allColumns = rowNode.children;
        allColumns.forEach((colNode, colIndex) => {
          const { rowRef, colRef } = colNode;
          if (baseRowIndex === rowRef && baseColIndex === colRef) {
            cellsToDelete.push({ rowIndex, columnIndex: colIndex });
          }
        });
      });
    });
  }

  return cellsToDelete;
};

export const withTableV2 =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { insertBreak, normalizeNode, deleteBackward, deleteForward } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];
      switch (node?.type) {
        case TableElement.TABLE: {
          // if table doesn't have any tr -> remove it
          if (node.children.some((n) => n?.type === TableElement.TR) === false) {
            Transforms.removeNodes(editor, { at: path });
            return;
          }

          const { hasColumnHeading, hasRowHeading } = node;
          if (hasColumnHeading) {
            for (
              let rowIndex = 0;
              rowIndex < ((node?.children as any[]) || []).length;
              rowIndex++
            ) {
              const row = node.children[rowIndex];
              for (let columnIndex = 0; columnIndex < row?.children?.length; columnIndex++) {
                const column = row.children[columnIndex];
                if (columnIndex === 0 && column.type !== TableElement.TH) {
                  Transforms.setNodes(
                    editor,
                    { type: TableElement.TH },
                    {
                      at: [...path, rowIndex, columnIndex],
                    }
                  );
                  return;
                }
              }
            }
          }
          if (hasRowHeading) {
            const row = node.children[0];
            for (let columnIndex = 0; columnIndex < row.children.length; columnIndex++) {
              const column = row.children[columnIndex];
              if (column.type !== TableElement.TH) {
                Transforms.setNodes(
                  editor,
                  { type: TableElement.TH },
                  {
                    at: [...path, 0, columnIndex],
                  }
                );
                return;
              }
            }
          }

          for (const [child, childPath] of Node.children(editor, path)) {
            if (Element.isElement(child) && child?.type !== TableElement.TR) {
              Transforms.removeNodes(editor, { at: childPath });
              return;
            }
          }

          // Merge all cells that have fullRowSpan or fullColSpan
          const allRows = node.children;
          const fullColumnCells = [];
          allRows.forEach((rowNode, rowIndex) => {
            const allColumns = rowNode.children;
            allColumns?.forEach((colNode, colIndex) => {
              if (colNode.colSpan === allColumns.length && colNode.rowSpan > 1) {
                fullColumnCells.push({ columnIndex: colIndex, rowIndex, rowSpan: colNode.rowSpan });
              }
            });
          });

          const colCellsToDelete = getCellToDelete({ cells: fullColumnCells, allRows });

          if (colCellsToDelete.length > 0) {
            reverse(colCellsToDelete).forEach(({ rowIndex, columnIndex }) => {
              Transforms.removeNodes(editor, { at: [...path, rowIndex, columnIndex] });
            });
            fullColumnCells.forEach(({ rowIndex, columnIndex }) => {
              Transforms.setNodes(editor, { rowSpan: 1 }, { at: [...path, rowIndex, columnIndex] });
            });
            return;
          }
          break;
        }
        case TableElement.TR: {
          for (const [child, childPath] of Node.children(editor, path)) {
            if (
              Element.isElement(child) &&
              child?.type !== TableElement.TD &&
              child?.type !== TableElement.TH
            ) {
              Transforms.removeNodes(editor, { at: childPath });
              return;
            }
          }

          // if all cell is hidden -> row empty -> remove
          if ((node?.children || []).every((item) => item.hidden) && path[path.length - 1] > 0) {
            const [prevRow, prevPath] = Editor.node(editor, Path.previous(path));
            if (prevRow) {
              prevRow.children.forEach((colNode, colIndex) => {
                if (colNode.rowSpan) {
                  Transforms.setNodes(
                    editor,
                    { rowSpan: Math.max(colNode.rowSpan - 1, 0) },
                    { at: [...prevPath, colIndex] }
                  );
                }
              });
            }
            Transforms.removeNodes(editor, { at: path });

            return;
          }

          const [parentNode]: any = Editor.parent(editor, path);
          // make sure row always have same column set on table
          if (parentNode?.cellsWidth) {
            const cellCount = max(
              (parentNode.children as any).map((row) => row?.children?.length || 0)
            );
            const rowCellCount = (node.children as any).length;
            if (rowCellCount < cellCount) {
              Transforms.insertNodes(editor, createTd(), { at: [...path, rowCellCount] });
              return;
            }
          }

          break;
        }
        case TableElement.TH:
        case TableElement.TD: {
          break;
        }
      }

      normalizeNode(entry);
    };

    editor.deleteBackward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'backward',
        match: matchTableContent,
      });
      if (!canDelete) {
        return;
      }
      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'forward',
        match: matchTableContent,
      });
      if (!canDelete) {
        return;
      }
      deleteForward(unit);
    };

    editor.insertBreak = () => {
      const { selection } = editor;
      if (selection) {
        const [table] = Editor.nodes(editor, {
          at: editor.selection,
          match: (n: any) => n?.type === TableElement.TABLE,
        });

        if (table) {
          editor.insertText('\n');
          return;
        }
      }

      insertBreak();
    };

    return editor;
  };
